/**
 * Issue Confirmation modal (design "Modals") — final, forward-only issue.
 * Summary box + consequences checklist + warn box. Confirm → onConfirm().
 */
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import ModalShell from './modalShell';
import { useTheme } from '../../context/themeContext';
import SkIcon from '../skIcon';

interface Props {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  variant: string;
  customer: string;
  grandTotal: string;
  number?: string;
  /** Document word, e.g. "invoice", "bill". Defaults to "invoice". */
  docWord?: string;
  /** Label for the party row in the summary. Defaults to "Customer". */
  partyLabel?: string;
  /** Override the consequences checklist (purchase docs post differently). */
  consequences?: string[];
}

const CONSEQUENCES = [
  'Assign the next number from the INV series',
  'Post a balanced journal entry to the ledger (AR · Income · GST Output)',
  'Lock the document — it becomes read-only and immutable',
  'Queue the e-invoice JSON for IRP export (IRN captured on success)',
];

const IssueModal: React.FC<Props> = ({ visible, onClose, onConfirm, variant, customer, grandTotal, number = 'Assigned on issue', docWord = 'invoice', partyLabel = 'Customer', consequences = CONSEQUENCES }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title={`Issue this ${docWord}?`}
      subtitle="Issuing is final and forward-only"
      headerTone="accent"
      width={480}
      footer={
        <>
          <Pressable onPress={onClose} style={{ height: 34, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.line, borderRadius: 5, justifyContent: 'center' }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable onPress={onConfirm} style={{ height: 34, paddingHorizontal: 16, borderRadius: 5, justifyContent: 'center', backgroundColor: colors.accent }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: '#fff' }}>Issue {docWord}</Text>
          </Pressable>
        </>
      }
    >
      <View style={{ gap: 14 }}>
        {/* summary box */}
        <View style={{ padding: 13, borderRadius: 8, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, gap: 6 }}>
          {[['Variant', variant], [partyLabel, customer], ['Grand total', grandTotal], ['Assigned number', number]].map(([l, v], i) => (
            <View key={l} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3 }}>{l}</Text>
              <Text style={{ fontFamily: i >= 2 ? font.monoSemibold : font.sansSemibold, fontSize: 12, fontWeight: '600', color: colors.ink }}>{v}</Text>
            </View>
          ))}
        </View>
        {/* consequences */}
        <View style={{ gap: 8 }}>
          {consequences.map((c) => (
            <View key={c} style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
              <View style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                <SkIcon name="check" size={10} color={colors.accent} strokeWidth={3} />
              </View>
              <Text style={{ flex: 1, fontFamily: font.sans, fontSize: 12, color: colors.ink2, lineHeight: 17 }}>{c}</Text>
            </View>
          ))}
        </View>
        {/* warn box */}
        <View style={{ flexDirection: 'row', gap: 8, padding: 11, borderRadius: 5, backgroundColor: colors.warnSoft, borderWidth: 1, borderColor: colors.warnLine }}>
          <SkIcon name="alert" size={14} color={colors.warn} strokeWidth={2.2} />
          <Text style={{ flex: 1, fontFamily: font.sans, fontSize: 12, color: colors.warn, lineHeight: 17 }}>
            After issuing there is no Edit — correct via Void &amp; reissue or a Credit note.
          </Text>
        </View>
      </View>
    </ModalShell>
  );
};

export default IssueModal;
